import { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import StatCard from '../../ui/statCard/statCard.component';
import CustomDataGrid from '../../ui/customDataGrid/customDataGrid.component';
import {
  BREAKDOWN_JOBS_ENDPOINT,
  BREAKDOWN_JOB_ISSUES_ENDPOINT,
  BREAKDOWN_JOB_SERVICES_ENDPOINT,
  BREAKDOWN_TECHNICIANS_ENDPOINT,
  BREAKDOWN_USED_PARTS_ENDPOINT,
  getJobStatusLabelKey,
} from '../../../consts';
import { EquipmentFullServiceStats } from '../../../types/analytics';
import { DateRangeValue } from '../../ui/dateRangeFilter/dateRangeFilter.component';
import { formatDate } from '../../../utils';
import EntityReferenceLink from '../../ui/entityReference/entityReferenceLink.component';

interface EquipmentServiceStatsContentProps {
  equipmentId: string | number;
  stats: EquipmentFullServiceStats;
  dateRange: DateRangeValue;
  canViewJobs: boolean;
}

export default function EquipmentServiceStatsContent({
  equipmentId,
  stats,
  dateRange,
  canViewJobs,
}: EquipmentServiceStatsContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const scopeParams = useMemo(
    () => ({
      equipment_id: equipmentId,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    }),
    [equipmentId, dateRange.start_date, dateRange.end_date]
  );
  const scopeKey = `${equipmentId}-${dateRange.start_date}-${dateRange.end_date}`;

  const technicianColumns: GridColDef[] = [
    { field: 'name', headerName: t('Technician'), flex: 1, minWidth: 160 },
    { field: 'assigned_jobs_count', headerName: t('Assigned Jobs'), width: 150 },
  ];

  const jobIssueColumns: GridColDef[] = [
    { field: 'title', headerName: t('Issue'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const jobServiceColumns: GridColDef[] = [
    { field: 'title', headerName: t('Service'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const usedPartColumns: GridColDef[] = [
    { field: 'part_name', headerName: t('Part'), flex: 1, minWidth: 160 },
    { field: 'quantity', headerName: t('Quantity'), width: 120 },
    { field: 'value', headerName: t('Value (zł)'), width: 120 },
  ];

  const jobColumns: GridColDef[] = [
    {
      field: 'number',
      headerName: t('Number'),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="job"
          entityId={params.row.id}
          label={params.row.number}
          canPreview={canViewJobs}
        />
      ),
    },
    {
      field: 'status',
      headerName: t('Status'),
      width: 150,
      valueGetter: (value) => t(getJobStatusLabelKey(value) || value),
    },
    {
      field: 'created_at',
      headerName: t('Created'),
      width: 140,
      valueGetter: (value) => formatDate(value),
    },
    {
      field: 'finished_at',
      headerName: t('Finished'),
      width: 140,
      valueGetter: (value) => formatDate(value) || '-',
    },
    {
      field: 'assigned_to',
      headerName: t('Assigned To'),
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: string[]) => (value?.length ? value.join(', ') : '-'),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <StatCard label={t('Jobs in Selected Period')} value={stats.jobs_in_range} />
        <StatCard
          label={t('Used Parts')}
          value={`${stats.used_parts.count} (${stats.used_parts.total_value} zł)`}
        />
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('Most Common Technicians')}
          </Typography>
          <CustomDataGrid
            key={scopeKey}
            url={BREAKDOWN_TECHNICIANS_ENDPOINT}
            columns={technicianColumns}
            extraParams={scopeParams}
            onRowClick={(id) => navigate(`/service/statistics/technicians/${id}`)}
            dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25] }}
          />
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Job Issues')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_JOB_ISSUES_ENDPOINT}
              columns={jobIssueColumns}
              extraParams={scopeParams}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25] }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Job Services')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_JOB_SERVICES_ENDPOINT}
              columns={jobServiceColumns}
              extraParams={scopeParams}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25] }}
            />
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('Used Parts')}
          </Typography>
          <CustomDataGrid
            key={scopeKey}
            url={BREAKDOWN_USED_PARTS_ENDPOINT}
            columns={usedPartColumns}
            extraParams={scopeParams}
            dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25] }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('Recent Jobs')}
          </Typography>
          <CustomDataGrid
            key={scopeKey}
            url={BREAKDOWN_JOBS_ENDPOINT}
            columns={jobColumns}
            extraParams={scopeParams}
            onRowClick={(id) => navigate(`/service/jobs/${id}`)}
            dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25] }}
          />
        </CardContent>
      </Card>
    </>
  );
}
